// utility/TimeZoneTamperDetection.jsx
import React, { useEffect, useState } from 'react';
import { formatDateTime, formatDateTimeDifference } from '../components/Date';

const TimeZoneTamperDetection = ({ serverTimeData, onRetry }) => {
    const [detectedIssues, setDetectedIssues] = useState([]);
    const [retryCount, setRetryCount] = useState(0);
    const [isChecking, setIsChecking] = useState(false);
    const [timeDiffMs, setTimeDiffMs] = useState(0);

    useEffect(() => {
        const issues = [];
        const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Compare with server data
        if (serverTimeData) {
            // Calculate time difference
            const diff = Math.abs(serverTimeData.serverTime - Date.now());
            setTimeDiffMs(diff);

            // Check time difference using Asia/Kolkata as primary
            if (diff > 300000) { // 5 minutes threshold
                const formattedDiff = formatDateTimeDifference(diff);
                issues.push(`System time is off by ${formattedDiff}`);
            } else {
                // If time is within 5 minutes, no issue
                // This allows up to 6 hours difference without blocking
            }

            // Check if timezone matches stored preference
            const storedTimezone = localStorage.getItem('expected_timezone');
            if (storedTimezone && storedTimezone !== currentTimezone) {
                issues.push(`Timezone changed from ${storedTimezone} to ${currentTimezone}`);
            }
        }

        setDetectedIssues(issues);
    }, [serverTimeData, retryCount]);

    const handleFixTime = async () => {
        setIsChecking(true);

        setTimeout(() => {
            if (onRetry) {
                onRetry();
            }
            setIsChecking(false);
        }, 1000);
    };

    const handleManualRetry = () => {
        setRetryCount(prev => prev + 1);
        if (onRetry) {
            onRetry();
        }
    };


    const handleRetryBackup = () => {
        setIsChecking(true);
        setRetryCount(prev => prev + 1);

        setTimeout(() => {
            if (onRetry) {
                onRetry();
            }
            setIsChecking(false);
        }, 1000);
    };

    const handleRetry = async () => {
        setIsChecking(true);
        setRetryCount(prev => prev + 1);

        // Call onRetry immediately without delay
        if (onRetry) {
            await onRetry(); // Wait for the check to complete
        }

        // Small delay just for UI feedback
        setTimeout(() => {
            setIsChecking(false);
        }, 500);
    };

    // Determine if we should show warning (time diff > 5 minutes) or info (time diff <= 6 hours)
    const showError = timeDiffMs > 21600000; // More than 6hrs minutes
    const showWarning = timeDiffMs > 300000; // More than 5 minutes
    const showInfo = timeDiffMs <= 300000 && timeDiffMs > 0; // Within 5 minutes but not zero

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
        }}>
            <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '40px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}>
                {/* {showWarning ? (
                    <>
                        <div style={{ fontSize: '64px', textAlign: 'center', marginBottom: '20px' }}>⚠️</div>
                        <h2 style={{ color: '#d32f2f', textAlign: 'center', marginBottom: '20px' }}>
                            System Time Tampering Detected
                        </h2>
                        <p style={{ color: '#666', textAlign: 'center', marginBottom: '30px' }}>
                            We've detected that your system time may have been modified. This can affect critical functionality of the portal.
                        </p>
                    </>
                ) : showInfo ? (
                    <>
                        <div style={{ fontSize: '64px', textAlign: 'center', marginBottom: '20px' }}>🕒</div>
                        <h2 style={{ color: '#ff9800', textAlign: 'center', marginBottom: '20px' }}>
                            Time Difference Detected
                        </h2>
                        <p style={{ color: '#666', textAlign: 'center', marginBottom: '30px' }}>
                            Your system time is slightly different from server time. This is just a notification.
                        </p>
                    </>
                ) : null} */}
                {/* Update the icon/title section to use isNotification prop */}
                {showWarning || showInfo ? (
                    <>
                        <div style={{ fontSize: '64px', textAlign: 'center', marginBottom: '20px' }}>
                            {showWarning ? '⚠️' : '🕒'}
                        </div>
                        <h2 style={{ color: showWarning ? '#d32f2f' : '#ff9800', textAlign: 'center', marginBottom: '20px' }}>
                            {showWarning ? 'System Time Tampering Detected' : 'Time Difference Detected'}
                        </h2>
                        <p style={{ color: '#666', textAlign: 'center', marginBottom: '30px' }}>
                            {showWarning
                                ? 'We\'ve detected that your system time may have been modified. This can affect critical functionality of the portal.'
                                : 'Your system time is slightly different from server time. This is just a notification.'}
                        </p>
                    </>
                ) : null}

                <div style={{ background: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                    <p><strong>Server Time :</strong> {
                        serverTimeData?.asia_kolkata?.datetime
                            ? formatDateTime(serverTimeData?.asia_kolkata?.datetime).longdatetime
                            : 'Unknown'
                    }</p>
                    <p><strong>Server Timezone:</strong> {serverTimeData?.asia_kolkata?.timezone || 'Unknown'}</p>

                    <p><strong>Your System Time:</strong> {formatDateTime(new Date().toISOString()).longdatetime}</p>

                    {/* <p><strong>Server Time (IST):</strong> {serverTimeData?.asia_kolkata?.datetime || 'Unknown'}</p>
                    <p><strong>Your System Time:</strong> {new Date().toLocaleString()}</p> */}

                    <p><strong>Your Timezone:</strong> {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
                    {timeDiffMs > 0 && (
                        <p><strong>Time Difference:</strong> {formatDateTimeDifference(timeDiffMs)}</p>
                    )}
                </div>

                {detectedIssues.length > 0 && (
                    <div style={{
                        background: showWarning ? '#fff3f3' : '#fff8e1',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px'
                    }}>
                        <h4 style={{ color: showWarning ? '#d32f2f' : '#ff9800', marginBottom: '10px' }}>
                            {showWarning ? 'Issues Detected:' : 'Information:'}
                        </h4>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {detectedIssues.map((issue, index) => (
                                <li key={index} style={{ color: '#666', marginBottom: '5px' }}>{issue}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexDirection: 'column' }}>
                    <button
                        style={{
                            background: showWarning ? '#d32f2f' : '#ff9800',
                            color: 'white',
                            padding: '12px 24px',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isChecking ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: '500',
                            opacity: isChecking ? 0.7 : 1
                        }}
                        // onClick={handleFixTime}
                        onClick={handleRetry}
                        disabled={isChecking}
                    >
                        {isChecking ? 'Checking...' : showWarning ? "I've Fixed My System Time" : "OK, Continue"}
                    </button>

                    {/* <button
                        style={{
                            background: 'transparent',
                            color: '#666',
                            padding: '8px 16px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                        onClick={handleManualRetry}
                    >
                        Retry Check
                    </button> */}

                    <p style={{ fontSize: '12px', color: '#999', textAlign: 'center', marginTop: '10px' }}>
                        {showWarning
                            ? 'After fixing your system time, click the button above to re-verify.'
                            : 'Small time differences are allowed. Click Continue to access the portal.'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TimeZoneTamperDetection;

